// onboarding-flows.ts — the flow registry, the per-user seen-state store and
// the first-visit trigger, shared by both clients (WORK ITEM 63 PR 2).
//
// FLOWS ARE DATA: each client registers the FlowDefs for its own surfaces
// (the console its home screen, the Helm its in-release views) over the
// shared engine in onboarding.ts. This module owns everything that is not
// surface-specific:
//
//   - registerFlows / flowsForSurface / getFlow — the typed registry.
//   - createOnboardingStore — seen-state: SERVER-SIDE per user (the product
//     has real accounts; localStorage alone replays every tour on a second
//     browser), riding the ui_state 'onboarding' seat ({ [flowId]: epoch-ms },
//     key pinned 'global', migration 062). localStorage is the offline echo.
//     FAIL-OPEN in the reference's direction: no server lane AND no local
//     lane → everything reads seen — a broken store must never nag, and a
//     standalone dev Bridge (where /__console/* GETs answer 200 with SPA
//     HTML, not 404 — measured, helm-bridge/src/server/index.ts static
//     fallthrough) degrades to the localStorage lane.
//   - installFirstVisitFlows — the router hook: call it with the surface key
//     on every route render. First visit of a surface (per the store) starts
//     that surface's flow after a short settle delay; navigating away from a
//     live flow dismisses it (dismissal persists — the user moved on, and
//     replaying a half-finished tour on every return is nagging, not help).
//
// Persistence rule (engine contract): every end reason persists EXCEPT
// 'no-targets' — a flow that never rendered anything was not seen.

import {
  activeFlowId,
  dismissActive,
  startFlow,
  type FlowDef,
  type FlowHandle,
} from './onboarding.js';

// --------------------------------------------------------------------------
// Registry.
// --------------------------------------------------------------------------

const REGISTRY = new Map<string, FlowDef>();

/** Register flow definitions. Duplicate ids refuse (first wins) and log. */
export function registerFlows(defs: FlowDef[], log: (l: string) => void = (l) => console.info(l)): void {
  for (const def of defs) {
    if (REGISTRY.has(def.id)) {
      log(`[onboarding] registerFlows: duplicate flow id '${def.id}' refused (first registration wins)`);
      continue;
    }
    REGISTRY.set(def.id, def);
  }
}

export function getFlow(id: string): FlowDef | null {
  return REGISTRY.get(id) ?? null;
}

export function flowsForSurface(surface: string): FlowDef[] {
  return [...REGISTRY.values()].filter((f) => f.surface === surface);
}

/** Every registered flow id. The assistant's screen-context snapshot ships
 * this as `available_flows` so overlay_start_flow can name a real one
 * instead of guessing (work item 64). */
export function registeredFlowIds(): string[] {
  return [...REGISTRY.keys()];
}

/** Every DOM id any registered step spotlights — the product's own curated
 * set of pointable controls. The assistant's snapshot filters this to the
 * ids actually present and ships it as `spotlight_targets`. */
export function registeredTargetIds(): string[] {
  const ids = new Set<string>();
  for (const f of REGISTRY.values()) {
    for (const step of f.steps) {
      for (const t of step.targets) ids.add(t.id);
    }
  }
  return [...ids];
}

/** Measured inventory of everything registered — for logs and drills. */
export function flowInventory(): { flows: number; steps: number; surfaces: number } {
  const defs = [...REGISTRY.values()];
  return {
    flows: defs.length,
    steps: defs.reduce((n, f) => n + f.steps.length, 0),
    surfaces: new Set(defs.map((f) => f.surface)).size,
  };
}

// --------------------------------------------------------------------------
// Seen-state store.
// --------------------------------------------------------------------------

export interface OnboardingStoreOpts {
  /** The seat URL: '/api/ui-state/onboarding/global' on the console,
   * '/__console/api/ui-state/onboarding/global' in the Helm client. */
  seatUrl: string;
  /** localStorage echo key (namespaced per client). */
  localKey: string;
  /** Injectable for drills. MUST be pre-bound when passed (fetch.bind). */
  fetchFn?: typeof fetch;
  log?: (line: string) => void;
}

export interface OnboardingStore {
  /** Resolve ALWAYS (never rejects); idempotent — callers can await freely. */
  load(): Promise<void>;
  /** True when the flow was seen — or when NO storage lane works at all
   * (fail-open: a broken store never nags). */
  seen(flowId: string): boolean;
  /** Local echo immediately; server PUT fire-and-forget (LWW, no rev — a
   * racing tab loses only a benign seen-flag, and the flow simply never
   * replays once either write lands). */
  markSeen(flowId: string): void;
  snapshot(): Record<string, number>;
}

const isSeenMap = (v: unknown): v is Record<string, number> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export function createOnboardingStore(opts: OnboardingStoreOpts): OnboardingStore {
  const log = opts.log ?? ((l: string) => console.info(l));
  const fetchFn = opts.fetchFn ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined);
  const map: Record<string, number> = {};
  let serverLane = false;
  let localLane = false;
  let loading: Promise<void> | null = null;

  const readLocal = (): void => {
    try {
      const raw = localStorage.getItem(opts.localKey);
      localLane = true;
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (isSeenMap(parsed)) {
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v === 'number') map[k] = Math.max(map[k] ?? 0, v);
        }
      }
    } catch {
      localLane = false;
    }
  };

  const writeLocal = (): void => {
    if (!localLane) return;
    try {
      localStorage.setItem(opts.localKey, JSON.stringify(map));
    } catch {
      localLane = false;
    }
  };

  const load = (): Promise<void> => {
    if (loading) return loading;
    loading = (async () => {
      readLocal();
      if (!fetchFn) return;
      try {
        const res = await fetchFn(opts.seatUrl, { headers: { accept: 'application/json' } });
        if (!res.ok) {
          log(`[onboarding] seen-state server lane off (${res.status}) — ${localLane ? 'localStorage echo only' : 'NO storage lane: fail-open, all flows read seen'}`);
          return;
        }
        // A standalone Bridge answers /__console/* GETs 200 with the SPA
        // HTML — json() throws there, and the catch keeps us on the echo.
        const body: unknown = await res.json();
        const state = (body as { state?: unknown }).state;
        if (state !== null && !isSeenMap(state)) return;
        serverLane = true;
        if (isSeenMap(state)) {
          for (const [k, v] of Object.entries(state)) {
            if (typeof v === 'number') map[k] = Math.max(map[k] ?? 0, v);
          }
        }
        writeLocal();
      } catch {
        log(`[onboarding] seen-state server lane off (unreachable) — ${localLane ? 'localStorage echo only' : 'NO storage lane: fail-open, all flows read seen'}`);
      }
    })();
    return loading;
  };

  const put = (): void => {
    if (!serverLane || !fetchFn) return;
    void fetchFn(opts.seatUrl, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ state: map }),
    }).catch(() => {
      // The local echo already has it; the next session's load re-merges.
    });
  };

  return {
    load,
    seen: (flowId: string): boolean => {
      if (map[flowId] !== undefined) return true;
      // Fail-open: with no storage lane at all, never nag.
      return !serverLane && !localLane;
    },
    markSeen: (flowId: string): void => {
      map[flowId] = Date.now();
      writeLocal();
      put();
    },
    snapshot: (): Record<string, number> => ({ ...map }),
  };
}

// --------------------------------------------------------------------------
// First-visit trigger.
// --------------------------------------------------------------------------

/** The first-visit courtesy delay (owner ruling, item 63 follow-up):
 * "let's delay the start of the flow by 3 seconds ... that way the user can
 * get a sense of the layout of the page before the onboarding flow pops up
 * and obfuscates the page." Applies ONLY to auto-triggered first-visit
 * flows — a programmatic spotlight/startFlow call means NOW (the entrance
 * animation rides the engine and plays for both). */
export const FIRST_VISIT_DELAY_MS = 3000;

export interface FirstVisitOpts {
  /** Settle delay before a flow fires (default FIRST_VISIT_DELAY_MS). */
  delayMs?: number;
  log?: (line: string) => void;
  /** Drill seam: injected startFlow/dismiss/active (defaults = the engine). */
  engine?: {
    startFlow: typeof startFlow;
    dismissActive: typeof dismissActive;
    activeFlowId: typeof activeFlowId;
  };
}

/**
 * Returns the ROUTE HOOK: call it with the surface key on every route
 * render. Contract:
 *   - navigating to a different surface while a flow is live dismisses the
 *     flow ('dismissed' — persisted; the user moved on);
 *   - the first not-yet-seen flow registered for the new surface starts
 *     after delayMs, unless by then the user navigated again, a flow is
 *     already active, or the store (settled by its own load) says seen;
 *   - everything is a no-op for surfaces with no registered flow.
 */
export function installFirstVisitFlows(
  store: OnboardingStore,
  opts: FirstVisitOpts = {},
): (surface: string) => void {
  const delayMs = opts.delayMs ?? FIRST_VISIT_DELAY_MS;
  const log = opts.log ?? ((l: string) => console.info(l));
  const engine = opts.engine ?? { startFlow, dismissActive, activeFlowId };
  let visitToken = 0;

  return (surface: string): void => {
    const token = ++visitToken;
    if (engine.activeFlowId()) {
      // The engine persists through the flow's own onFlowEnd hook below.
      engine.dismissActive('dismissed');
    }
    const candidates = flowsForSurface(surface);
    if (candidates.length === 0) return;
    void store.load().then(() => {
      const def = candidates.find((f) => !store.seen(f.id));
      if (!def) return;
      setTimeout(() => {
        if (token !== visitToken) return; // navigated on — this visit is stale
        if (engine.activeFlowId()) return; // one flow at a time, first wins
        if (store.seen(def.id)) return; // another tab finished it meanwhile
        const handle: FlowHandle | null = engine.startFlow(def, {
          log,
          onFlowEnd: (flowId, reason, info) => {
            log(`[onboarding] ${flowId}: ${reason} (${info.shownSteps}/${info.totalSteps} shown, ${info.missingSteps} skipped-missing)`);
            if (reason !== 'no-targets') store.markSeen(flowId);
          },
        });
        if (!handle) log(`[onboarding] ${def.id}: did not start (refused or ended synchronously)`);
      }, delayMs);
    });
  };
}
